<template>
  <div class="grid">
    <div class="col-12">
      <Card>
        <template #title>
          <div
            class="flex align-items-center justify-content-between flex-wrap">
            <span>Histórico Funcional (Indiretas)</span>
            <Button
              label="Imprimir"
              icon="pi pi-print"
              class="p-button-outlined p-button-sm noPrint"
              @click="imprimir" />
          </div>
        </template>
        <template #content>
          <div class="ficha">
            <!-- Cabeçalho -->
            <div class="hd">
              <img src="@/img/logo-codata.png" class="hd-logo" alt="CODATA" />
              <div
                class="hd-row flex flex-column md:flex-row md:align-items-start">
                <div class="flex-1">
                  <span class="small">CODATA · CNPJ 09.189.499/0001-00</span>
                  <span class="small">
                    Av. João da Mata, 200 — Jaguaribe, João Pessoa/PB · CEP
                    58015-020
                  </span>
                  <h1>Histórico Funcional</h1>
                </div>
                <div class="hd-m">
                  <span class="small">Formulário 2.007.001</span>
                  <span class="small">Emitida em {{ emitidaEm }}</span>
                </div>
              </div>
            </div>

            <!-- Identificação -->
            <div class="ident flex flex-column sm:flex-row">
              <div class="photo">
                <img
                  v-if="servidor.foto"
                  :src="servidor.foto"
                  :alt="`Foto de ${servidor.nome || 'servidor'}`" />
                <svg
                  v-else
                  width="80"
                  height="96"
                  viewBox="0 0 80 96"
                  fill="none"
                  stroke="#9aa2ab"
                  stroke-width="2"
                  aria-label="Foto 3x4">
                  <circle cx="40" cy="36" r="18"></circle>
                  <path d="M6 96c0-20 15-34 34-34s34 14 34 34"></path>
                </svg>
              </div>
              <div class="flex-1 flex flex-column gap-3">
                <div class="name flex align-items-center flex-wrap gap-2">
                  <b>{{ servidor.nome }}</b>
                  <span v-if="servidor.situacao" class="st ok">{{
                    servidor.situacao
                  }}</span>
                </div>
                <div class="grid campos">
                  <div
                    v-for="campo in identificacao"
                    :key="campo.label"
                    class="col-12 sm:col-6 md:col-4 field">
                    <span class="l">{{ campo.label }}</span>
                    <span class="v" :class="{ muted: !campo.valor }">
                      {{ campo.valor || 'Não informado' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- KPIs -->
            <div class="grid kpis">
              <div
                v-for="kpi in kpis"
                :key="kpi.label"
                class="col-12 sm:col-6 md:col-3 kpi">
                <span class="l">{{ kpi.label }}</span>
                <b>{{ kpi.valor }}</b>
                <span class="small">{{ kpi.detalhe }}</span>
              </div>
            </div>

            <!-- Seções de campos (1 a 5) -->
            <div v-for="sec in secoesCampos" :key="sec.numero" class="sec">
              <div class="sec-h">
                <span class="sec-n">{{ sec.numero }}</span>
                <h2>{{ sec.titulo }}</h2>
                <span v-if="sec.aside" class="aside">{{ sec.aside }}</span>
              </div>
              <div class="grid campos">
                <div
                  v-for="campo in sec.campos"
                  :key="campo.label"
                  class="col-12 sm:col-6 md:col-4 field"
                  :class="campo.classe">
                  <span class="l">{{ campo.label }}</span>
                  <span class="v" :class="{ muted: !campo.valor }">
                    {{ campo.valor || 'Não informado' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 6. Dependentes -->
            <div class="sec">
              <div class="sec-h">
                <span class="sec-n">6</span>
                <h2>Dependentes e beneficiários</h2>
                <span class="aside">{{ dependentes.length }} registros</span>
              </div>
              <DataTable
                :value="dependentes"
                class="p-datatable-sm"
                :loading="carregandoDependentes"
                responsive-layout="scroll">
                <template #empty>Nenhum dependente registrado</template>
                <Column field="nome" header="Nome" />
                <Column field="parentesco" header="Parentesco" />
                <Column field="nascimento" header="Nascimento" />
                <Column field="estadoCivil" header="Estado civil" />
              </DataTable>
            </div>

            <!-- 7. Histórico de cargos -->
            <div class="sec">
              <div class="sec-h">
                <span class="sec-n">7</span>
                <h2>Histórico de cargos</h2>
                <span class="aside">
                  {{ historicoCargos.length }} registros · do mais recente ao
                  mais antigo
                </span>
              </div>
              <DataTable
                :value="historicoCargos"
                class="p-datatable-sm"
                :loading="carregandoHistorico"
                responsive-layout="scroll">
                <template #empty>Nenhum histórico de cargo encontrado</template>
                <Column field="data" header="Data" class="nw" />
                <Column field="cargo" header="Cargo / função" />
                <Column header="Motivo">
                  <template #body="{ data }">
                    <span :class="{ muted: !data.motivo }">
                      {{ data.motivo || 'Não informado' }}
                    </span>
                  </template>
                </Column>
              </DataTable>
            </div>

            <!-- 8. Afastamentos -->
            <div class="sec">
              <div class="sec-h">
                <span class="sec-n">8</span>
                <h2>Afastamentos</h2>
                <span class="aside">{{ afastamentos.length }} registro(s)</span>
              </div>
              <DataTable
                :value="afastamentos"
                class="p-datatable-sm"
                :loading="carregandoAfastamentos"
                responsive-layout="scroll">
                <template #empty>Nenhum afastamento registrado</template>
                <Column field="tipo" header="Tipo" />
                <Column field="inicio" header="Início" class="nw" />
                <Column field="fim" header="Fim" class="nw" />
                <Column header="Duração" class="num">
                  <template #body="{ data }">{{ data.duracao }} dias</template>
                </Column>
                <Column header="Observações">
                  <template #body="{ data }">
                    <span :class="{ muted: !data.observacoes }">
                      {{ data.observacoes || 'Não informado' }}
                    </span>
                  </template>
                </Column>
              </DataTable>
            </div>

            <!-- 9. Histórico salarial -->
            <div class="sec">
              <div class="sec-h">
                <span class="sec-n">9</span>
                <h2>Histórico salarial</h2>
                <span class="aside">
                  {{ historicoSalarial.length }} registros · do mais recente ao
                  mais antigo
                </span>
              </div>
              <DataTable
                :value="historicoSalarial"
                class="p-datatable-sm"
                :loading="carregandoSalarial"
                responsive-layout="scroll">
                <template #empty>Nenhum histórico salarial encontrado</template>
                <Column field="data" header="Data" class="nw" />
                <Column header="Salário-base" class="num">
                  <template #body="{ data }">
                    {{ data.salario | formatarMoeda }}
                  </template>
                </Column>
                <Column header="Motivo">
                  <template #body="{ data }">
                    <span :class="{ muted: !data.motivo }">
                      {{ data.motivo || 'Não informado' }}
                    </span>
                  </template>
                </Column>
              </DataTable>
            </div>

            <!-- 10. Férias -->
            <div class="sec">
              <div class="sec-h">
                <span class="sec-n">10</span>
                <h2>Férias</h2>
                <span class="aside">
                  {{ ferias.length }} períodos · do mais recente ao mais antigo
                </span>
              </div>
              <DataTable
                :value="ferias"
                class="p-datatable-sm"
                :loading="carregandoFerias"
                responsive-layout="scroll">
                <template #empty>Nenhum período de férias encontrado</template>
                <Column
                  field="aquisitivo"
                  header="Período aquisitivo"
                  class="nw" />
                <Column header="Período de gozo" class="nw">
                  <template #body="{ data }">{{ data.gozo || '—' }}</template>
                </Column>
                <Column header="Dias" class="num">
                  <template #body="{ data }">{{ data.dias || '—' }}</template>
                </Column>
                <Column header="Situação">
                  <template #body="{ data }">
                    <span class="st" :class="data.situacao.tipo">
                      {{ data.situacao.texto }}
                    </span>
                  </template>
                </Column>
              </DataTable>
            </div>

            <!-- 11. Endereços anteriores -->
            <div class="sec">
              <div class="sec-h">
                <span class="sec-n">11</span>
                <h2>Endereços anteriores</h2>
                <span class="aside">do mais recente ao mais antigo</span>
              </div>
              <DataTable
                :value="enderecosAnteriores"
                class="p-datatable-sm"
                :loading="carregandoEnderecos"
                responsive-layout="scroll">
                <template #empty>Nenhum endereço anterior registrado</template>
                <Column field="logradouro" header="Logradouro" />
                <Column field="bairro" header="Bairro" />
                <Column field="cidade" header="Cidade" />
              </DataTable>
            </div>

            <!-- Assinaturas -->
            <div class="sign grid">
              <div class="col-12 md:col-6">
                <div class="sign-line">
                  <b>Responsável pela posse</b>
                  <span class="small">Nome, cargo e data</span>
                </div>
              </div>
              <div class="col-12 md:col-6">
                <div class="sign-line">
                  <b>Servidor empossado</b>
                  <span class="small">
                    {{ servidor.nome }} · Mat. {{ servidor.matricula }}
                  </span>
                </div>
              </div>
            </div>

            <div
              class="foot flex flex-column md:flex-row justify-content-between">
              <span>Emitida em {{ emitidaEm }}</span>
              <span
                >Documento de uso interno — contém dados pessoais (LGPD)</span
              >
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import FichaRegistroService from '@/service/FichaRegistroService'
import MatriculaService from '@/service/MatriculaService'
import { matriculaStore } from '@/stores/matricula'

export default {
  filters: {
    formatarMoeda(valor) {
      return Number(valor).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
  },

  beforeRouteEnter(to, from, next) {
    const store = matriculaStore()
    const m = store.matriculaSelecionada
    if (
      m &&
      m.entidadeNome &&
      (m.entidadeNome === 'SEAD 1' || m.entidadeNome === 'SEAD 2')
    ) {
      next('/')
    } else {
      next()
    }
  },

  setup() {
    const store = matriculaStore()
    return { store }
  },

  data() {
    return {
      emitidaEm: '',
      carregandoDadosPessoais: false,
      carregandoHistorico: false,
      carregandoFerias: false,
      carregandoEnderecos: false,
      carregandoDependentes: false,
      carregandoSalarial: false,
      carregandoAfastamentos: false,
      servidor: {
        nome: '',
        matricula: '',
        situacao: '',
        cpf: '',
        foto: '',
      },
      identificacao: [],
      kpis: [],
      secoesCampos: [],
      dependentes: [],
      historicoCargos: [],
      afastamentos: [],
      historicoSalarial: [],
      ferias: [],
      enderecosAnteriores: [],
    }
  },

  watch: {
    'store.matriculaSelecionada': function () {
      if (!this.verificarAcesso()) {
        return
      }
      this.carregarDados()
    },
  },

  created() {
    this.fichaRegistroService = new FichaRegistroService(this.$http)
    this.matriculaService = new MatriculaService(this.$http)
    this.emitidaEm = new Date().toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  },

  mounted() {
    if (!this.verificarAcesso()) {
      return
    }
    this.carregarDados()
  },

  methods: {
    imprimir() {
      const cpf = this.getCpfLimpo()
      const tituloOriginal = document.title
      document.title = `historico_funcional_${cpf}`
      window.print()
      document.title = tituloOriginal
    },
    verificarAcesso() {
      const m = this.store.matriculaSelecionada
      if (
        m &&
        m.entidadeNome &&
        (m.entidadeNome === 'SEAD 1' || m.entidadeNome === 'SEAD 2')
      ) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Histórico Funcional disponível apenas para indiretas.',
          life: 5000,
        })
        this.$router.push('/')
        return false
      }
      return true
    },
    getCpfLimpo() {
      const cpf =
        (this.store.matriculaSelecionada &&
          this.store.matriculaSelecionada.cpf) ||
        this.servidor.cpf
      return (cpf || '').replace(/\D/g, '')
    },
    converterFotoHexParaBase64(foto) {
      if (!foto) return ''

      const texto = foto.trim()

      if (texto.startsWith('data:')) {
        return texto
      }

      const prefixosBase64 = ['/9j/', 'iVBORw0KGgo', 'R0lGOD', 'UklGR']
      const temCaracteresBase64 = /[+/=]/.test(texto)
      const ehBase64 =
        temCaracteresBase64 || prefixosBase64.some((p) => texto.startsWith(p))

      if (ehBase64 && /^[A-Za-z0-9+/=]+$/.test(texto)) {
        let mime = 'image/jpeg'
        if (texto.startsWith('iVBORw0KGgo')) mime = 'image/png'
        else if (texto.startsWith('R0lGOD')) mime = 'image/gif'
        else if (texto.startsWith('UklGR')) mime = 'image/webp'
        return `data:${mime};base64,${texto}`
      }

      const hex = texto.replace(/^0x/i, '').replace(/\s/g, '')
      if (!hex || hex.length % 2 !== 0 || !/^[0-9a-f]+$/i.test(hex)) {
        return ''
      }

      const assinatura = hex.slice(0, 24).toUpperCase()
      let mime = ''
      if (assinatura.startsWith('FFD8FF')) mime = 'image/jpeg'
      else if (assinatura.startsWith('89504E470D0A1A0A')) mime = 'image/png'
      else if (
        assinatura.startsWith('474946383761') ||
        assinatura.startsWith('474946383961')
      )
        mime = 'image/gif'
      else if (
        assinatura.startsWith('52494646') &&
        hex.slice(16, 24).toUpperCase() === '57454250'
      )
        mime = 'image/webp'
      if (!mime) return ''

      let binario = ''
      for (let i = 0; i < hex.length; i += 2) {
        binario += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16))
      }
      return `data:${mime};base64,${btoa(binario)}`
    },
    async carregarDados() {
      if (!this.getCpfLimpo()) {
        try {
          const matriculas =
            await this.matriculaService.getMatriculasUsuarioLogado()
          if (matriculas && matriculas.length) {
            this.servidor.cpf = matriculas[0].cpf
          }
        } catch (err) {
          const msg =
            err.response?.data?.message ||
            'Erro ao carregar matrículas do usuário.'
          this.$toast.add({
            severity: 'error',
            summary: msg,
            life: 10000,
          })
          return
        }
      }
      const cpf = this.getCpfLimpo()
      if (!cpf) {
        return
      }
      await this.carregarDadosPessoais(cpf)
      this.carregarHistoricoFuncao(cpf)
      this.carregarFerias(cpf)
      this.carregarEnderecos(cpf)
      this.carregarDependentes(cpf)
      this.carregarHistoricoSalarial(cpf)
      this.carregarAfastamentos(cpf)
    },
    async carregarDadosPessoais(cpf) {
      this.carregandoDadosPessoais = true
      try {
        const d = await this.fichaRegistroService.getDadosPessoais(cpf)
        if (!d) {
          return
        }
        this.servidor = {
          nome: d.nome || '',
          matricula: d.chapa || '',
          situacao: d.situacao === 'A' ? 'Ativo' : d.situacao || '',
          cpf: d.cpf || this.servidor.cpf,
          foto: this.converterFotoHexParaBase64(d.foto),
        }
        this.identificacao = [
          { label: 'Matrícula', valor: d.chapa || '' },
          { label: 'Nº de ordem', valor: String(d.numeroFichaRegistro || '') },
          {
            label: 'Admissão',
            valor: d.dataAdmissao
              ? new Date(d.dataAdmissao).toLocaleDateString('pt-BR')
              : '',
          },
          { label: 'Cargo atual', valor: d.cargo || '' },
          { label: 'Classe / nível', valor: d.codFuncao || '' },
          { label: 'Lotação', valor: d.lotacao || '' },
        ]
        this.kpis = [
          {
            label: 'Salário-base atual',
            valor: this.formatarMoeda(d.salario),
            detalhe: d.recebimento || '',
          },
          {
            label: 'Salário-hora',
            valor: this.formatarMoeda(d.salarioHora),
            detalhe: '',
          },
          {
            label: 'Jornada',
            valor: d.jornada ? `${d.jornada} horas mensais` : '',
            detalhe: '',
          },
        ]
        const campos = [
          {
            numero: 1,
            titulo: 'Vínculo funcional',
            campos: [
              {
                label: 'Data de admissão',
                valor: d.dataAdmissao
                  ? new Date(d.dataAdmissao).toLocaleDateString('pt-BR')
                  : '',
              },
              {
                label: 'Opção pelo FGTS',
                valor: d.dataOpcaoFgts
                  ? new Date(d.dataOpcaoFgts).toLocaleDateString('pt-BR')
                  : '',
              },
              { label: 'Forma de pagamento', valor: d.recebimento || '' },
              {
                label: 'Jornada',
                valor: d.jornada ? `${d.jornada} horas mensais` : '',
              },
              {
                label: 'Salário-base',
                valor: this.formatarMoeda(d.salario),
              },
              {
                label: 'Salário-hora',
                valor: this.formatarMoeda(d.salarioHora),
              },
            ],
          },
          {
            numero: 2,
            titulo: 'Dados pessoais',
            campos: [
              {
                label: 'Data de nascimento',
                valor: d.dataNascimento
                  ? new Date(d.dataNascimento).toLocaleDateString('pt-BR')
                  : '',
              },
              {
                label: 'Naturalidade',
                valor:
                  d.naturalidade && d.estadoNatal
                    ? `${d.naturalidade} — ${d.estadoNatal}`
                    : d.naturalidade || '',
              },
              {
                label: 'Nacionalidade',
                valor: d.nacionalidade || '',
              },
              { label: 'Sexo', valor: d.sexo || '' },
              {
                label: 'Estado civil',
                valor: d.estadoCivilDescricao || d.estadoCivil || '',
              },
              { label: 'Escolaridade', valor: d.escolaridade || '' },
            ],
          },
          {
            numero: 3,
            titulo: 'Documentos',
            campos: [
              { label: 'CPF', valor: d.cpf || '' },
              {
                label: 'Identidade (RG)',
                valor: d.carteiraIdentidade
                  ? `${d.carteiraIdentidade} · ${
                      d.orgaoEmissorIdentidade || ''
                    }/${d.estadoNatal || ''} · ${
                      d.dataEmissaoIdentidade
                        ? new Date(d.dataEmissaoIdentidade).toLocaleDateString(
                            'pt-BR',
                          )
                        : ''
                    }`
                  : '',
              },
              {
                label: 'PIS/PASEP',
                valor: d.pisPasep || '',
              },
              {
                label: 'Título de eleitor',
                valor: d.tituloEleitor
                  ? `${d.tituloEleitor} · zona ${
                      d.zonaTituloEleitor || ''
                    } · seção ${d.secaoTituloEleitor || ''}`
                  : '',
              },
              {
                label: 'Certificado de reservista',
                valor: d.certificadoReservista || '',
              },
              { label: 'CNH', valor: d.carteiraMotorista || '' },
              {
                label: 'Categoria (reservista)',
                valor: d.categoriaMilitar || '',
              },
              {
                label: 'Categoria (CNH)',
                valor: d.tipoCarteiraHabilitacao || '',
              },
              { label: 'Registro profissional', valor: '' },
            ],
          },
          {
            numero: 4,
            titulo: 'Contato e endereço',
            aside: 'Endereços anteriores na seção 11',
            campos: [
              { label: 'E-mail institucional', valor: d.email || '' },
              { label: 'Telefone fixo', valor: d.telefone1 || '' },
              { label: 'Celular', valor: d.telefone2 || '' },
              {
                label: 'Endereço atual',
                valor: [
                  d.rua,
                  d.numeroRua,
                  d.complemento,
                  d.bairro,
                  d.cidade,
                  d.estado,
                ]
                  .filter(Boolean)
                  .join(', '),
                classe: 'md:col-8',
              },
              { label: 'CEP', valor: d.cep || '' },
            ],
          },
          {
            numero: 5,
            titulo: 'Quando estrangeiro',
            aside: 'Não aplicável — servidor de nacionalidade brasileira',
            campos: [
              {
                label: 'Data de chegada',
                valor: d.dataChegada
                  ? new Date(d.dataChegada).toLocaleDateString('pt-BR')
                  : '',
              },
              { label: 'Tipo de visto', valor: d.tipoVisto || '' },
              {
                label: 'Cônjuge brasileiro',
                valor: d.conjugeBrasil != null ? String(d.conjugeBrasil) : '',
              },
              {
                label: 'Naturalizado',
                valor: d.naturalizado != null ? String(d.naturalizado) : '',
              },
              {
                label: 'Nº de filhos',
                valor:
                  d.numeroFilhosBrasil != null
                    ? String(d.numeroFilhosBrasil)
                    : '',
              },
              {
                label: 'Nº de registro geral',
                valor: d.numeroRegistroGeral || '',
              },
              { label: 'Nº do decreto', valor: d.numeroDecreto || '' },
              { label: 'Nº da carteira de identidade', valor: '' },
              { label: 'Validade da carteira de identidade', valor: '' },
              { label: 'Validade da carteira de trabalho', valor: '' },
            ],
          },
        ]
        this.secoesCampos = campos
      } catch (err) {
        const msg =
          err.response?.data?.message || 'Erro ao carregar dados pessoais.'
        this.$toast.add({
          severity: 'error',
          summary: msg,
          life: 10000,
        })
      } finally {
        this.carregandoDadosPessoais = false
      }
    },
    async carregarHistoricoFuncao(cpf) {
      this.carregandoHistorico = true
      try {
        const res = await this.fichaRegistroService.getHistoricoFuncao(cpf)
        this.historicoCargos = res.map((item) => ({
          data: item.dataMudanca
            ? new Date(item.dataMudanca).toLocaleDateString('pt-BR')
            : '',
          cargo: item.valorNovo || '',
          motivo: item.descricaoMotivo || '',
        }))
      } catch (err) {
        const msg =
          err.response?.data?.message || 'Erro ao carregar histórico de cargos.'
        this.$toast.add({
          severity: 'error',
          summary: msg,
          life: 10000,
        })
      } finally {
        this.carregandoHistorico = false
      }
    },
    async carregarFerias(cpf) {
      this.carregandoFerias = true
      try {
        const res = await this.fichaRegistroService.getHistoricoFerias(cpf)
        this.ferias = res.map((item) => {
          const inicioAq = item.inicioPeriodoAquisitivo
            ? new Date(item.inicioPeriodoAquisitivo).toLocaleDateString('pt-BR')
            : ''
          const fimAq = item.fimPeriodoAquisitivo
            ? new Date(item.fimPeriodoAquisitivo).toLocaleDateString('pt-BR')
            : ''
          const inicioFerias = item.inicioFerias
            ? new Date(item.inicioFerias).toLocaleDateString('pt-BR')
            : ''
          const fimFerias = item.fimFerias
            ? new Date(item.fimFerias).toLocaleDateString('pt-BR')
            : ''
          const gozado = !!(inicioFerias && fimFerias)
          return {
            aquisitivo: inicioAq && fimAq ? `${inicioAq} a ${fimAq}` : '',
            gozo: gozado ? `${inicioFerias} a ${fimFerias}` : '',
            dias: item.numeroDias || null,
            situacao: gozado
              ? { tipo: 'ok', texto: 'Gozadas' }
              : { tipo: 'warn', texto: 'Sem gozo registrado' },
          }
        })
      } catch (err) {
        const msg =
          err.response?.data?.message || 'Erro ao carregar histórico de férias.'
        this.$toast.add({
          severity: 'error',
          summary: msg,
          life: 10000,
        })
      } finally {
        this.carregandoFerias = false
      }
    },
    async carregarEnderecos(cpf) {
      this.carregandoEnderecos = true
      try {
        const res = await this.fichaRegistroService.getHistoricoEndereco(cpf)
        this.enderecosAnteriores = res.map((item) => ({
          logradouro: [item.rua, item.numero].filter(Boolean).join(', '),
          bairro: item.bairro || '',
          cidade: `${item.cidade || ''} — ${item.estado || ''}`,
        }))
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          'Erro ao carregar endereços anteriores.'
        this.$toast.add({
          severity: 'error',
          summary: msg,
          life: 10000,
        })
      } finally {
        this.carregandoEnderecos = false
      }
    },
    async carregarDependentes(cpf) {
      this.carregandoDependentes = true
      try {
        const res = await this.fichaRegistroService.getDependentes(cpf)
        this.dependentes = res.map((item) => ({
          nome: item.nome || '',
          parentesco: item.grauParentesco || '',
          nascimento: item.dataNascimento
            ? new Date(item.dataNascimento).toLocaleDateString('pt-BR')
            : '',
          estadoCivil: item.estadoCivil || '',
        }))
      } catch (err) {
        const msg =
          err.response?.data?.message || 'Erro ao carregar dependentes.'
        this.$toast.add({
          severity: 'error',
          summary: msg,
          life: 10000,
        })
      } finally {
        this.carregandoDependentes = false
      }
    },
    async carregarHistoricoSalarial(cpf) {
      this.carregandoSalarial = true
      try {
        const res = await this.fichaRegistroService.getHistoricoSalarial(cpf)
        this.historicoSalarial = res.map((item) => ({
          data: item.dataMudanca
            ? new Date(item.dataMudanca).toLocaleDateString('pt-BR')
            : '',
          salario: item.salario || 0,
          motivo: item.motivo || '',
        }))
      } catch (err) {
        const msg =
          err.response?.data?.message || 'Erro ao carregar histórico salarial.'
        this.$toast.add({
          severity: 'error',
          summary: msg,
          life: 10000,
        })
      } finally {
        this.carregandoSalarial = false
      }
    },
    async carregarAfastamentos(cpf) {
      this.carregandoAfastamentos = true
      try {
        const res = await this.fichaRegistroService.getAfastamentos(cpf)
        this.afastamentos = res.map((item) => ({
          tipo: item.tipo || '',
          inicio: item.inicio
            ? new Date(item.inicio).toLocaleDateString('pt-BR')
            : '',
          fim: item.fim ? new Date(item.fim).toLocaleDateString('pt-BR') : '',
          duracao: item.duracaoDias || 0,
          observacoes: 'Não informado',
        }))
      } catch (err) {
        const msg =
          err.response?.data?.message || 'Erro ao carregar afastamentos.'
        this.$toast.add({
          severity: 'error',
          summary: msg,
          life: 10000,
        })
      } finally {
        this.carregandoAfastamentos = false
      }
    },

    formatarMoeda(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
  },
}
</script>

<style scoped>
.ficha {
  --navy: #1d3a5f;
  --ink: #1b1f24;
  --grey: #5a6068;
  --muted: #6b7179;
  --rule: #b9c0c8;
  --rule-soft: #dde1e6;
  --head: #eef1f5;
  --warn: #8a4b00;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.ficha h1 {
  font-size: 22px;
  line-height: 28px;
  font-weight: 600;
  color: var(--navy);
  margin: 4px 0 0;
}
.ficha h2 {
  font-size: 17px;
  font-weight: 600;
  color: var(--navy);
  margin: 0;
}
.small,
.aside,
.foot,
.l {
  font-size: 12px;
  line-height: 16px;
  color: var(--grey);
  display: block;
}
.hd {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--rule);
}
.hd-logo {
  width: 200px;
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0;
}
.hd-row {
  gap: 14px;
}
.hd-m {
  display: flex;
  flex-direction: column;
}
@media (min-width: 768px) {
  .hd-m {
    align-items: flex-end;
    text-align: right;
  }
}
.ident {
  gap: 20px;
  align-items: stretch;
}
.photo {
  width: 96px;
  min-height: 120px;
  flex-shrink: 0;
  border: 1px solid var(--rule);
  border-radius: 4px;
  background: #f3f5f8;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
@media (min-width: 576px) {
  .photo {
    align-self: stretch;
  }
}
.photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.name b {
  font-size: 22px;
  line-height: 28px;
  word-break: break-word;
}
.campos.grid {
  row-gap: 0;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-top: 4px;
  padding-bottom: 4px;
}
.v {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  word-break: break-word;
}
.muted {
  color: var(--muted);
  font-style: italic;
  font-weight: 400;
}
.kpis.grid {
  margin: 0;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: #f6f8fb;
}
.kpi {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 14px;
  border-bottom: 1px solid #d3d8de;
}
.kpi b {
  font-size: 16px;
  line-height: 24px;
  color: var(--navy);
}
@media (min-width: 768px) {
  .kpi {
    border-bottom: 0;
    border-right: 1px solid #d3d8de;
  }
  .kpi:last-child {
    border-right: 0;
  }
}
.sec {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sec-h {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 6px;
  border-bottom: 1.5px solid var(--navy);
}
.sec-n {
  font-size: 12px;
  font-weight: 700;
  color: var(--navy);
  letter-spacing: 0.04em;
}
.aside {
  margin-left: auto;
}
.flag {
  display: block;
  font-size: 12px;
  color: var(--warn);
}
.st {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
  border: 1px solid;
  white-space: nowrap;
}
.ok {
  color: #1e5b33;
  border-color: #8fbf9f;
  background: #eef7f1;
}
.warn {
  color: #7a3d00;
  border-color: #e0b27a;
  background: #fdf4e8;
}
.info {
  color: var(--navy);
  border-color: #9fb3cc;
  background: #eef3f9;
}
.ficha ::v-deep .p-datatable .p-datatable-thead > tr > th {
  background: var(--head);
  font-size: 12px;
  border-color: var(--rule);
}
.ficha ::v-deep .p-datatable .p-datatable-tbody > tr > td {
  font-size: 12.5px;
  border-color: var(--rule-soft);
  vertical-align: top;
}
.ficha ::v-deep .nw {
  white-space: nowrap;
}
.ficha ::v-deep .num {
  text-align: right;
  white-space: nowrap;
}
.sign {
  padding-top: 32px;
}
.sign-line {
  border-top: 1px solid var(--ink);
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sign-line b {
  font-size: 12px;
}
.foot {
  gap: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--rule);
}

@media print {
  .noPrint {
    display: none !important;
  }
  .p-card,
  .p-card-body,
  .p-card-content {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
  }
  .ficha {
    gap: 12px !important;
    font-size: 12px !important;
  }
  .ident {
    flex-direction: row !important;
  }
  .grid {
    display: flex !important;
    flex-wrap: wrap !important;
  }
  [class*='col-12'] {
    flex: 0 0 auto !important;
    width: 100% !important;
  }
  [class*='md:col-2'] {
    width: 16.6667% !important;
  }
  [class*='md:col-3'] {
    width: 25% !important;
  }
  [class*='md:col-4'] {
    width: 33.3333% !important;
  }
  [class*='md:col-6'] {
    width: 50% !important;
  }
  [class*='sm:col-6'] {
    width: 50% !important;
  }
  .sec,
  .kpis,
  .ident,
  .sign {
    break-inside: avoid;
  }
  .ficha ::v-deep .p-datatable .p-datatable-tbody > tr {
    break-inside: avoid;
  }
}
@page {
  size: A4;
  margin: 12mm;
}
</style>
